import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { ET_TIMEZONE, PreprocedureTaskCode } from './constants';
import { loadPreProcedureInputs } from './load';
import { PreProcedureInputs, TaskRef } from './types';

export interface UsePreProcedureDataResult {
  inputs: PreProcedureInputs | undefined;
  isLoading: boolean;
  error: Error | undefined;
  refresh: () => void;
  patchTask: (patientId: string, code: PreprocedureTaskCode, task: TaskRef) => void;
  patchComment: (appointmentId: string, comment: string | null) => void;
}

export function usePreProcedureData(): UsePreProcedureDataResult {
  const medplum = useMedplum();
  const [inputs, setInputs] = useState<PreProcedureInputs | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    // Start of today in ET, matching the ops team's working day rather than the viewer's browser.
    const fromInstant = dayjs().tz(ET_TIMEZONE).startOf('day').toISOString();
    loadPreProcedureInputs(medplum, fromInstant)
      .then((result) => {
        if (!cancelled) {
          setInputs(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, refreshKey]);

  const patchTask = useCallback((patientId: string, code: PreprocedureTaskCode, task: TaskRef) => {
    setInputs((prev) =>
      prev ? { ...prev, tasks: { ...prev.tasks, [patientId]: { ...prev.tasks[patientId], [code]: task } } } : prev
    );
  }, []);

  const patchComment = useCallback((appointmentId: string, comment: string | null) => {
    setInputs((prev) =>
      prev
        ? {
            ...prev,
            appointments: prev.appointments.map((a) => (a.appointmentId === appointmentId ? { ...a, comment } : a)),
          }
        : prev
    );
  }, []);

  return { inputs, isLoading, error, refresh, patchTask, patchComment };
}
